"use client";

import { motion } from "framer-motion";
import { ClipboardCheck } from "lucide-react";
import CriteriaTable from "@/components/unis/CriteriaTable";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import type { University } from "@/lib/universities";
import { admissionTimeline } from "@/lib/university-details";

const QUOTAS = [
  {
    short: "ABQ",
    share: "30 %",
    name: "Abiturbestenquote",
    text: "Hier zählt ausschließlich dein Abiturergebnis – verglichen wird innerhalb deines Bundeslandes.",
  },
  {
    short: "AdH",
    share: "60 %",
    name: "Auswahlverfahren der Hochschulen",
    text: "Jede Hochschule gewichtet eigene Kriterien – von Abitur über TMS bis zu Ausbildung und Diensten.",
  },
  {
    short: "ZEQ",
    share: "10 %",
    name: "Zusätzliche Eignungsquote",
    text: "Das Abitur spielt keine Rolle – es zählen Kriterien wie TMS, Ausbildung, Dienste oder Interviews.",
  },
];

function Timeline({ steps }: { steps: { title: string; text: string }[] }) {
  return (
    <ol className="relative flex flex-col gap-0">
      {steps.map((step, i) => (
        <li key={step.title} className="relative flex gap-4 pb-7 last:pb-0">
          {/* Linie */}
          {i < steps.length - 1 && (
            <span
              className="absolute left-[15px] top-8 bottom-0 w-px bg-line"
              aria-hidden="true"
            />
          )}
          <span className="relative z-10 w-8 h-8 shrink-0 rounded-full bg-accent-soft text-accent text-sm font-bold flex items-center justify-center tnum">
            {i + 1}
          </span>
          <div className="flex flex-col gap-1 pt-1">
            <h4 className="font-display font-semibold text-base text-ink">{step.title}</h4>
            <p className="text-sm text-ink-soft leading-relaxed">{step.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function AdmissionSection({ university }: { university: University }) {
  const isPublic = university.type === "Staatlich";

  return (
    <motion.section
      variants={stagger(0.09)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-labelledby="admission-heading"
    >
      <motion.p
        variants={fadeUp}
        className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-accent mb-3"
      >
        <ClipboardCheck size={14} aria-hidden="true" />
        Zulassung
      </motion.p>
      <motion.h2
        variants={fadeUp}
        id="admission-heading"
        className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mb-5"
      >
        Zulassung &amp; Auswahlkriterien
      </motion.h2>

      {isPublic ? (
        <>
          <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed mb-6 max-w-2xl">
            Die Studienplätze an der {university.name} werden zentral über
            hochschulstart.de in drei Quoten vergeben. Welche Kriterien die
            Hochschule dabei berücksichtigt, zeigt die Tabelle.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            {QUOTAS.map((quota) => (
              <motion.div
                key={quota.short}
                variants={fadeUp}
                className="card-flat flex flex-col gap-2 bg-white/60 p-5"
              >
                <span className="flex items-baseline gap-2">
                  <span className="font-display font-bold text-2xl text-accent tnum">
                    {quota.share}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-faint">
                    {quota.short}
                  </span>
                </span>
                <h3 className="font-semibold text-sm text-ink">{quota.name}</h3>
                <p className="text-xs text-ink-faint leading-relaxed">{quota.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} className="mb-10">
            <CriteriaTable university={university} />
          </motion.div>
        </>
      ) : (
        <motion.p variants={fadeUp} className="text-ink-soft leading-relaxed mb-8 max-w-2xl">
          Als private Hochschule vergibt die {university.name} ihre Studienplätze über
          ein eigenes Auswahlverfahren – unabhängig vom Numerus clausus. Statt der
          Abiturnote stehen Motivation, Persönlichkeit und oft Auswahltage oder
          Interviews im Mittelpunkt.
        </motion.p>
      )}

      <motion.h3
        variants={fadeUp}
        className="font-display font-bold text-xl text-ink mb-5"
      >
        So läuft deine Bewerbung
      </motion.h3>
      <motion.div variants={fadeUp}>
        <Timeline steps={admissionTimeline(university)} />
      </motion.div>
    </motion.section>
  );
}
